import React, { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import * as R from 'ramda'
import moment from 'moment'
import { Button, useMediaQuery } from '@material-ui/core'
import { DateRange } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useTheme } from '@material-ui/core/styles'
import { makeStyles } from '@material-ui/core/styles'
import { useIntl, FormattedMessage } from 'react-intl'

import Assets from 'app/configs/assetConfigs'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import SecondaryButton from 'app/components/buttons/SecondaryButton'

import { filterType } from './constants'
import { getDateFnsLocale, getLocalizedDateRange } from 'commons/date'
import customNavigationRenderer from 'app/componentV4/Filters/CustomRangeCalendarToolbar'

const useStyles = makeStyles((theme) => ({
  backgroundFilter: {
    backgroundColor: 'rgba(0, 115, 177, 0.06)',
    color: '#404040',
    '&:hover': {
      backgroundColor: 'rgba(0, 115, 177, 0.06)',
    },
  },
  backgroundActiveFilter: {
    backgroundColor: 'rgba(3, 114, 177, 1)',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: 'rgba(3, 114, 177, 1)',
    },
  },
  color: {
    color: '#404040',
  },
  textBalance: {
    fontSize: '20px',
    color: 'rgba(0, 178, 173, 1)',
  },

  rootDateRangPicker: {
    '& .rdrDateDisplayWrapper': { display: 'none' },
  },
}))

export default function Filter({
  onSelectedDate,
  dateRang,
  defaultFilter,
  onSelectedFilter,
  isLoading,
}) {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const classes = useStyles()
  const [isDateRang, setIsDateRang] = useState(false)
  const [selectedDate, setSelectedDate] = useState({})
  const isDownSM = useMediaQuery(theme.breakpoints.down('sm'))
  const fixedYears = moment().subtract(2, 'years').toDate()
  const today = moment().endOf('day').toDate()

  const filters = [
    {
      id: 1,
      key: filterType.ALL,
      label: formatMessage({
        id: 'planAndBilling.filter.all.label',
      }),
    },
    {
      id: 2,
      key: filterType.TODAY,
      label: formatMessage({
        id: 'planAndBilling.filter.today.label',
      }),
    },
    {
      id: 3,
      key: filterType.LAST_SEVEN_DAYS,
      label: formatMessage({
        id: 'planAndBilling.filter.week.later.label',
      }),
    },
    {
      id: 4,
      key: filterType.LAST_THIRTY_DAYS,
      label: formatMessage({
        id: 'planAndBilling.filter.month.later.label',
      }),
    },
    {
      id: 5,
      key: filterType.LAST_SIX_MONTHS,
      label: formatMessage({
        id: 'planAndBilling.filter.sixMonth.later.label',
      }),
    },
    {
      id: 6,
      key: filterType.LAST_YEAR,
      label: formatMessage({
        id: 'planAndBilling.filter.year.later.label',
      }),
    },
  ]

  const selectedDatePicker = (date) => {
    onSelectedDate(date)
    setIsDateRang(false)
  }

  const handleDateRangLable = useCallback(
    (selectedDate) => {
      return R.isNil(defaultFilter) || isDateRang
        ? getLocalizedDateRange(selectedDate.startDate, selectedDate.endDate)
        : formatMessage({
            id: 'planAndBilling.filter.rangDate.label',
          })
    },
    [defaultFilter, formatMessage, isDateRang],
  )

  const handleDateRang = (date) => {
    if (moment(date.startDate).isSame(date.endDate))
      selectedDatePicker({
        ...date,
        startDate: moment(date.startDate).startOf('day').toDate(),
        endDate: moment(date.endDate).endOf('day').toDate(),
      })
    else selectedDatePicker(date)
  }

  useEffect(() => {
    setSelectedDate(dateRang)
  }, [dateRang])

  return (
    <div className="w-full md:flex md:justify-between mb-10">
      <div>
        {filters.map((filter) => (
          <Button
            className={clsx(
              'my-3 md:my-0 font-500 mx-3 rounded-full text-12 px-10 normal-case',
              filter.key === defaultFilter
                ? classes.backgroundActiveFilter
                : classes.backgroundFilter,
              isLoading && filter.key === defaultFilter && 'text-white',
            )}
            disabled={isLoading}
            key={filter.key}
            onClick={() => onSelectedFilter(filter.key)}
          >
            {filter.label}
          </Button>
        ))}
      </div>
      <div className="my-3 md:my-0">
        <Button
          className={clsx(
            ' w-full md:w-224 font-500 md:mx-3 rounded-full text-12 px-10 normal-case',
            R.isNil(defaultFilter)
              ? classes.backgroundActiveFilter
              : classes.backgroundFilter,
          )}
          onClick={() => setIsDateRang(true)}
        >
          <div className="flex">
            <img
              alt="date rang icon"
              src={
                R.isNil(defaultFilter) ? Assets.carendarWhite : Assets.carendar
              }
              className="mr-3"
            />
            <div className="flex items-center">
              {handleDateRangLable(selectedDate ?? dateRang)}
            </div>
          </div>
        </Button>
        {isDateRang ? (
          <div className="bg-white border-1 p-3 rounded-8 absolute right-0 mx-10 z-99">
            <DateRange
              onChange={(item) => {
                setSelectedDate(item.selection)
              }}
              moveRangeOnFirstSelection={false}
              months={isDownSM ? 1 : 2}
              minDate={fixedYears}
              maxDate={today}
              ranges={[selectedDate]}
              rangeColors={['#0073b1']}
              direction="horizontal"
              className={classes.rootDateRangPicker}
              monthDisplayFormat="MMMM"
              locale={getDateFnsLocale()}
              navigatorRenderer={customNavigationRenderer}
            />
            <div className="flex justify-end mb-5">
              <SecondaryButton
                onClick={() => {
                  setIsDateRang(false)
                  setSelectedDate(dateRang)
                }}
              >
                <FormattedMessage id="action.cancel" />
              </SecondaryButton>
              <PrimaryButton
                className="mx-3"
                onClick={() => handleDateRang(selectedDate)}
              >
                <FormattedMessage id="action.submit" />
              </PrimaryButton>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
